import { Obj } from '../entity.js'
import { ObjOptions, InstanceObjOptions } from './base.js'

export interface ProcessObjOptions extends ObjOptions {
  timeout?: number
  memoryLimit?: number
  env?: { [key: string]: string }
  secrets?: { [key: string]: string }
}

export class ProcessObj extends Obj<'Process'> {
  readonly typeOf = 'Process' as const
  /** Function timeout in milliseconds */
  timeout?: number
  /** Memory limit in MB */
  memoryLimit?: number
  /** Environment variables specific to this function */
  env?: { [key: string]: string }
  /** Key-value pairs of secrets available to this object */
  secrets?: { [key: string]: string }

  public constructor(opts: ProcessObjOptions) {
    super({ ...opts, typeOf: 'Process' })
    if (opts.timeout !== undefined) {
      this.timeout = opts.timeout
    }
    if (opts.memoryLimit !== undefined) {
      this.memoryLimit = opts.memoryLimit
    }
    if (opts.env !== undefined) {
      this.env = opts.env
    }
    if (opts.secrets !== undefined) {
      this.secrets = opts.secrets
    }
  }
}

export function isProcessObj(obj: any): obj is ProcessObj {
  return obj.typeOf === 'Process'
}

export interface ProcessInstanceObjOptions extends InstanceObjOptions {
  startedAt?: number
  stoppedAt?: number
}

export class ProcessInstanceObj extends Obj<'ProcessInstance'> {
  readonly typeOf = 'ProcessInstance' as const
  startedAt?: number
  stoppedAt?: number

  public constructor(opts: ProcessInstanceObjOptions) {
    super({ ...opts, typeOf: 'ProcessInstance' })
    if (opts.startedAt !== undefined) {
      this.startedAt = opts.startedAt
    }
    if (opts.stoppedAt !== undefined) {
      this.stoppedAt = opts.stoppedAt
    }
  }
  override get instanceOfId(): string | undefined {
    if (this.instanceOf) {
      return `Process/${this.instanceOf}`
    }
    return undefined
  }
}

export function isProcessInstanceObj(obj: any): obj is ProcessInstanceObj {
  return obj.typeOf === 'ProcessInstance'
}

export interface FunctionObjOptions extends ObjOptions {
  timeout?: number
  memoryLimit?: number
  env?: { [key: string]: string }
  secrets?: { [key: string]: string }
}

export class FunctionObj extends Obj<'Function'> {
  readonly typeOf = 'Function' as const
  /** Function timeout in milliseconds */
  timeout?: number
  /** Memory limit in MB */
  memoryLimit?: number
  /** Environment variables specific to this function */
  env?: { [key: string]: string }
  /** Key-value pairs of secrets available to this object */
  secrets?: { [key: string]: string }

  public constructor(opts: FunctionObjOptions) {
    super({ ...opts, typeOf: 'Function' })
    if (opts.timeout !== undefined) {
      this.timeout = opts.timeout
    }
    if (opts.memoryLimit !== undefined) {
      this.memoryLimit = opts.memoryLimit
    }
    if (opts.env !== undefined) {
      this.env = opts.env
    }
    if (opts.secrets !== undefined) {
      this.secrets = opts.secrets
    }
  }
}

export function isFunctionObj(obj: any): obj is FunctionObj {
  return obj.typeOf === 'Function'
}

export interface FunctionInstanceObjOptions extends InstanceObjOptions {
  startedAt?: number
  stoppedAt?: number
}

export class FunctionInstanceObj extends Obj<'FunctionInstance'> {
  readonly typeOf = 'FunctionInstance' as const
  startedAt: number
  stoppedAt?: number

  public constructor(opts: FunctionInstanceObjOptions) {
    super({ ...opts, typeOf: 'FunctionInstance' })
    this.startedAt = opts.startedAt || Date.now()

    if (opts.stoppedAt !== undefined) {
      this.stoppedAt = opts.stoppedAt
    }
  }

  override get instanceOfId(): string | undefined {
    if (this.instanceOf) {
      return `Function/${this.instanceOf}`
    }
    return undefined
  }
}

export function isFunctionInstanceObj(obj: any): obj is FunctionInstanceObj {
  return obj.typeOf === 'FunctionInstance'
}

export interface ServiceObjOptions extends ObjOptions {
  env?: { [key: string]: string }
  secrets?: { [key: string]: string }
}

export class ServiceObj extends Obj<'Service'> {
  readonly typeOf = 'Service' as const
  /** Service type (e.g., 'http', 'grpc', 'websocket') */
  //type!: string
  /** Service port */
  //port?: number
  /** Service host */
  //host?: string
  /** Service health check endpoint */
  //healthCheckEndpoint?: string
  /** Service version */
  //version?: string
  /** Environment variables specific to this service */
  env?: { [key: string]: string }
  /** Secrets available to this service */
  secrets?: { [key: string]: string }
  /** Service scaling configuration */
  // scaling?: {
  //   minInstances?: number
  //   maxInstances?: number
  //   targetCPUUtilization?: number
  // }
  /** Service deployment configuration */
  // deployment?: {
  //   strategy?: 'rolling' | 'recreate' | 'blue-green'
  //   replicas?: number
  //   resources?: {
  //     cpu?: string
  //     memory?: string
  //   }
  // }

  public constructor(opts: ServiceObjOptions) {
    super({ ...opts, typeOf: 'Service' })
    if (opts.env !== undefined) {
      this.env = opts.env
    }
    if (opts.secrets !== undefined) {
      this.secrets = opts.secrets
    }
  }
}

export function isServiceObj(obj: any): obj is ServiceObj {
  return obj.typeOf === 'Service'
}
